<template>
    <section class="processes section-space">
        <div class="pbb-container processes__layout">
            <header class="processes__intro">
                <span class="eyebrow">Transparência processual</span>
                <h2>Principais processos em acompanhamento</h2>
                <p>
                    Informações, atos processuais e documentos organizados para uma consulta clara e
                    transparente.
                </p>
                <router-link to="/recuperacao-judicial" class="processes__all pbb-focus">
                    Explorar todos os processos
                    <v-icon size="19">mdi-arrow-right</v-icon>
                </router-link>
                <div class="processes__marker" aria-hidden="true">
                    <span>Processos em destaque</span>
                    <strong>{{ String(visibleProcesses.length).padStart(2, '0') }}</strong>
                </div>
            </header>

            <div class="processes__index">
                <div class="processes__index-head" aria-hidden="true">
                    <span>Índice</span>
                    <span>Empresa / processo</span>
                    <span>Natureza</span>
                </div>
                <component
                    v-for="(process, index) in visibleProcesses"
                    :key="process.id || process.name"
                    :is="hasExternalSite(process) ? 'a' : 'router-link'"
                    v-bind="caseLinkProps(process, processRoute(process))"
                    class="process-row pbb-focus"
                    :aria-label="
                        hasExternalSite(process)
                            ? `${process.name}: abre o site do caso em nova aba`
                            : undefined
                    "
                >
                    <span class="process-row__number">{{ formatIndex(index) }}</span>
                    <div class="process-row__identity">
                        <span class="process-row__logo">
                            <img
                                v-if="process.logo_url"
                                :src="process.logo_url"
                                :alt="process.name"
                            />
                            <span v-else>{{ initials(process.name) }}</span>
                        </span>
                        <span class="process-row__name">
                            <strong>{{ process.name }}</strong>
                            <small>{{
                                process.process_number || 'Consulte atos e documentos'
                            }}</small>
                        </span>
                    </div>
                    <span class="process-row__meta">
                        <small>{{ processLocation(process) }}</small>
                        <strong>{{ processType(process) }}</strong>
                    </span>
                    <span class="process-row__arrow"
                        ><v-icon size="21">{{
                            hasExternalSite(process) ? 'mdi-open-in-new' : 'mdi-arrow-top-right'
                        }}</v-icon></span
                    >
                </component>
            </div>
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import { getCaseType } from '@/common/case-types.data';
import { caseLinkProps, hasExternalSite } from '@/common/util/case-link';
export default {
    name: 'FeaturedProcessesSection',
    data: () => ({
        processes: [],
        fallbacks: [
            { id: '', name: 'Samarco Mineração S.A.', type: 1, state: 'MG' },
            { id: '', name: 'Marangoni Tread Latino America', type: 1, state: 'MG' },
            { id: '', name: 'Grupo Rossi', type: 1, state: 'SP' },
            { id: '', name: 'Usina Queiroz Júnior S.A.', type: 2, state: 'MG' }
        ]
    }),
    computed: {
        visibleProcesses() {
            return this.processes.length ? this.processes.slice(0, 6) : this.fallbacks;
        }
    },
    async created() {
        try {
            const response = await axios.get('/api/clients', { params: { limit: 6 } });
            this.processes = response.data.content.data || response.data.content || [];
        } catch (error) {
            this.processes = [];
        }
    },
    methods: {
        caseLinkProps,
        hasExternalSite,
        formatIndex(index) {
            return String(index + 1).padStart(2, '0');
        },
        processType(process) {
            return getCaseType(process.type).name;
        },
        processLocation(process) {
            const state = String(process.state || '').toUpperCase();
            return /^[A-Z]{2}$/.test(state) ? state : 'Brasil';
        },
        processRoute(process) {
            const { route } = getCaseType(process.type);
            return process.id ? `${route}/${process.id}` : route;
        },
        initials(name = '') {
            return name
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((word) => word[0])
                .join('');
        }
    }
};
</script>

<style scoped lang="scss">
.processes {
    overflow: hidden;
    background: var(--pbb-cream);
}
.processes__layout {
    display: grid;
    grid-template-columns: minmax(280px, 0.72fr) minmax(540px, 1.28fr);
    gap: clamp(70px, 9vw, 140px);
    align-items: start;
}
.processes__intro {
    position: sticky;
    top: 112px;
}
.processes__intro h2 {
    max-width: 510px;
    margin: 22px 0 26px;
    color: var(--pbb-navy);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: clamp(42px, 4.6vw, 64px);
    font-weight: 400;
    line-height: 1.04;
    letter-spacing: -0.06em;
}
.processes__intro > p {
    max-width: 440px;
    margin: 0;
    color: var(--pbb-muted);
    font-size: 15px;
    line-height: 1.75;
}
.processes__all {
    display: inline-flex;
    gap: 13px;
    align-items: center;
    margin-top: 36px;
    color: var(--pbb-navy);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-decoration: none;
    text-transform: uppercase;
}
.processes__all .v-icon {
    color: var(--pbb-copper);
    transition: transform 180ms ease;
}
.processes__all:hover .v-icon {
    transform: translateX(5px);
}
.processes__marker {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 440px;
    margin-top: 72px;
    padding-top: 18px;
    border-top: 1px solid #d8d4cc;
    color: #8b8a86;
    font-size: 9px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}
.processes__marker strong {
    color: var(--pbb-copper);
    font-size: 23px;
    font-weight: 400;
}
.processes__index {
    border-top: 1px solid #cbc7bf;
}
.processes__index-head {
    display: grid;
    grid-template-columns: 42px 1fr 145px;
    gap: 16px;
    padding: 15px 18px 15px 0;
    color: #96938c;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
}
.process-row {
    position: relative;
    z-index: 0;
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) 145px 42px;
    gap: 16px;
    align-items: center;
    min-height: 118px;
    padding: 20px 10px 20px 0;
    border-bottom: 1px solid #d8d4cc;
    color: var(--pbb-navy);
    text-decoration: none;
    transition: color 260ms ease, padding 260ms ease, background-color 260ms ease;
}
.process-row::before {
    position: absolute;
    z-index: -1;
    inset: 0 -18px;
    background: var(--pbb-navy);
    content: '';
    opacity: 0;
    transform: scaleY(0.82);
    transition: opacity 240ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}
.process-row:hover {
    padding-right: 2px;
    padding-left: 10px;
    color: white;
}
.process-row:hover::before {
    opacity: 1;
    transform: scaleY(1);
}
.process-row__number {
    color: var(--pbb-copper);
    font-size: 10px;
    font-weight: 700;
}
.process-row__identity {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr);
    gap: 18px;
    align-items: center;
}
.process-row__logo {
    display: grid;
    width: 54px;
    height: 54px;
    place-items: center;
    overflow: hidden;
    border: 1px solid #d8d4cc;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.52);
    color: var(--pbb-navy);
    font-size: 13px;
    font-weight: 700;
    transition: background-color 220ms ease;
}
.process-row:hover .process-row__logo {
    background: white;
}
.process-row__logo img {
    width: 80%;
    height: 80%;
    object-fit: contain;
}
.process-row__name strong,
.process-row__name small,
.process-row__meta strong,
.process-row__meta small {
    display: block;
}
.process-row__name strong {
    overflow: hidden;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.process-row__name small {
    margin-top: 7px;
    overflow: hidden;
    color: #8a8985;
    font-size: 9px;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 220ms ease;
}
.process-row:hover .process-row__name small,
.process-row:hover .process-row__meta small {
    color: rgba(255, 255, 255, 0.52);
}
.process-row__meta small {
    margin-bottom: 6px;
    color: #96938c;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}
.process-row__meta strong {
    font-size: 10px;
    font-weight: 700;
    line-height: 1.35;
}
.process-row__arrow {
    display: grid;
    width: 38px;
    height: 38px;
    place-items: center;
    border: 1px solid #cbc7bf;
    border-radius: 50%;
    transition: border-color 220ms ease, background-color 220ms ease, transform 220ms ease;
}
.process-row__arrow .v-icon {
    color: var(--pbb-navy);
    transition: color 220ms ease;
}
.process-row:hover .process-row__arrow {
    border-color: var(--pbb-copper);
    background: var(--pbb-copper);
    transform: rotate(45deg);
}
.process-row:hover .process-row__arrow .v-icon {
    color: white;
}
@media (max-width: 900px) {
    .processes__layout {
        grid-template-columns: 1fr;
        gap: 65px;
    }
    .processes__intro {
        position: static;
    }
    .processes__marker {
        display: none;
    }
}
@media (max-width: 620px) {
    .processes__intro h2 {
        font-size: 42px;
    }
    .processes__index-head {
        display: none;
    }
    .process-row {
        grid-template-columns: 28px minmax(0, 1fr) 38px;
        min-height: 108px;
        padding-right: 0;
    }
    .process-row__identity {
        grid-template-columns: 46px minmax(0, 1fr);
        gap: 12px;
    }
    .process-row__logo {
        width: 44px;
        height: 44px;
    }
    .process-row__name strong {
        font-size: 13px;
    }
    .process-row__meta {
        display: none;
    }
    .process-row__arrow {
        width: 34px;
        height: 34px;
    }
}
</style>
